import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, CheckCircle2, Globe } from 'lucide-react';
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { FadeUp, ScaleIn } from '../components/ScrollReveal';

const BEANS_IMG = 'https://customer-assets.emergentagent.com/job_bb23729c-0dea-4683-8280-e0aa97a36711/artifacts/rpuw479q_IMG_2753.jpeg';
const serif = { fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontWeight: 400 };

const geoUrl = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

const RealisticWorldMap = ({ lang }) => (
  <div className="relative w-full aspect-[2/1] bg-[#2B2016] rounded-2xl overflow-hidden border border-[#C9A84C]/10">
    <ComposableMap
      projection="geoMercator"
      projectionConfig={{ scale: 120 }}
      style={{ width: "100%", height: "100%" }}
    >
      <Geographies geography={geoUrl}>
        {({ geographies }) =>
          geographies.map((geo) => (
            <Geography
              key={geo.rsmKey}
              geography={geo}
              fill="#3D2F22"
              stroke="#4D3F32"
              strokeWidth={0.5}
              style={{
                default: { outline: "none" },
                hover: { fill: "#C9A84C", outline: "none" },
              }}
            />
          ))
        }
      </Geographies>
      <Marker coordinates={[7.266, 43.710]}>
        <circle r={4} fill="#C9A84C" />
        <text textAnchor="middle" y={-10} style={{ fontFamily: "Montserrat", fill: "#FAF7F2", fontSize: "8px", fontWeight: 600 }}>Nice</text>
      </Marker>
    </ComposableMap>
  </div>
);

const Contact = () => {
  const { lang } = useLanguage();
  const t = translations[lang].contact;
  const common = translations[lang].common;

  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    // COPIE ICI L'URL DE TON NOUVEAU SCRIPT (CELUI DU SHEET CONTACT)
    const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxuTkqBjY4nJmqaK8DdjrCYjZHaoQWgcOmu0rrh6gM-1_aDuhWWNB19ALfMfuaGWJ7KUg/exec";

    try {
      await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors', // Crucial pour Google Apps Script
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          subject: form.subject,
          message: form.message
        }),
      });

      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error("Erreur d'envoi:", error);
      setStatus('error');
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative min-h-[50vh] flex items-end pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={BEANS_IMG} alt="Contact hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C2416]/65" />
        </div>
        <div className="relative z-10 container-max pt-24 text-center w-full">
          <FadeUp>
            <p className="section-overline text-white/80">{t.overline}</p>
            <h1 className="text-5xl md:text-7xl text-white leading-tight mb-4" style={serif}>
              {t.heroTitle} <em className="not-italic text-[#E8D5A0]">{t.heroTitleAccent}</em>
            </h1>
            <p className="text-white/60 text-sm max-w-lg mx-auto">{t.heroDesc}</p>
          </FadeUp>
        </div>
      </section>

      <section className="section-padding bg-[#FAF7F2]">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-24">

            {/* Colonne Informations - Aération via space-y-10 */}
            <div className="lg:col-span-2 space-y-10">
              <FadeUp>
                <h3 className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#8B4513] mb-8">
                  {t.infoTitle}
                </h3>
                <div className="space-y-10">
                  <div className="flex items-start gap-5">
                    <div className="w-10 h-10 rounded-full bg-white border border-[#E5DDD2] flex items-center justify-center shrink-0">
                      <MapPin size={18} className="text-[#C9A84C]" />
                    </div>
                    <div>
                      <h4 className="text-[10px] uppercase tracking-widest text-[#7A6E63] mb-2">{t.addressLabel}</h4>
                      <p className="text-[#2C2416] font-medium leading-relaxed">{t.addressVal}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-10 h-10 rounded-full bg-white border border-[#E5DDD2] flex items-center justify-center shrink-0">
                      <Mail size={18} className="text-[#C9A84C]" />
                    </div>
                    <div>
                      <h4 className="text-[10px] uppercase tracking-widest text-[#7A6E63] mb-2">{t.emailLabel}</h4>
                      <a href={`mailto:${t.emailVal}`} className="text-[#2C2416] font-medium hover:text-[#8B4513] transition-colors leading-relaxed">
                        {t.emailVal}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-10 h-10 rounded-full bg-white border border-[#E5DDD2] flex items-center justify-center shrink-0">
                      <Phone size={18} className="text-[#C9A84C]" />
                    </div>
                    <div>
                      <h4 className="text-[10px] uppercase tracking-widest text-[#7A6E63] mb-2">{t.phoneLabel}</h4>
                      <div className="flex flex-col gap-1 leading-relaxed">
                        <a href="tel:+33789015050" className="text-[#2C2416] font-medium hover:text-[#8B4513] transition-colors">{t.phone1}</a>
                        <a href="tel:+33610257950" className="text-[#2C2416] font-medium hover:text-[#8B4513] transition-colors">{t.phone2}</a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-10 h-10 rounded-full bg-white border border-[#E5DDD2] flex items-center justify-center shrink-0">
                      <Clock size={18} className="text-[#C9A84C]" />
                    </div>
                    <div>
                      <h4 className="text-[10px] uppercase tracking-widest text-[#7A6E63] mb-2">{t.hoursTitle}</h4>
                      <p className="text-[#2C2416] font-medium leading-relaxed whitespace-pre-line">
                        {t.hoursVal}
                      </p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            </div>

            {/* Colonne Formulaire */}
            <div className="lg:col-span-3">
              <ScaleIn>
                <div className="bg-white border border-[#E5DDD2] rounded-3xl p-8 md:p-12 shadow-sm">
                  {status === 'success' ? (
                    <div className="text-center py-12">
                      <CheckCircle2 size={48} className="text-[#8B4513] mx-auto mb-4" />
                      <h3 className="text-2xl text-[#2C2416] mb-2" style={serif}>{common.successTitle}</h3>
                      <p className="text-[#7A6E63]">{common.successMsg}</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest mb-2 text-[#7A6E63] font-semibold">{t.labelName}</label>
                          <input type="text" name="name" value={form.name} onChange={handleChange} required
                            placeholder={t.placeholderName} className="input-light" />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest mb-2 text-[#7A6E63] font-semibold">{t.labelEmail}</label>
                          <input type="email" name="email" value={form.email} onChange={handleChange} required
                            placeholder={t.placeholderEmail} className="input-light" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest mb-2 text-[#7A6E63] font-semibold">{t.labelPhone}</label>
                          <input type="text" name="phone" value={form.phone} onChange={handleChange}
                            placeholder={t.placeholderPhone} className="input-light" />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest mb-2 text-[#7A6E63] font-semibold">{t.labelSubject}</label>
                          <input type="text" name="subject" value={form.subject} onChange={handleChange}
                            placeholder={t.placeholderSubject} className="input-light" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest mb-2 text-[#7A6E63] font-semibold">{t.labelMessage}</label>
                        <textarea name="message" value={form.message} onChange={handleChange} required rows={5}
                          placeholder={t.placeholderMessage} className="input-light resize-none" />
                      </div>
                      {status === 'error' && <p className="text-red-500 text-sm">{common.errorMsg}</p>}
                      <button type="submit" disabled={status === 'loading'}
                        className="btn-solid w-full justify-center disabled:opacity-60">
                        {status === 'loading' ? common.sending : common.send}
                      </button>
                    </form>
                  )}
                </div>
              </ScaleIn>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION CARTE */}
      <section className="section-padding bg-[#2B2016]">
        <div className="container-max">
          <FadeUp>
            <div className="flex items-center gap-3 mb-4">
              <Globe size={20} className="text-[#C9A84C]" />
              <h2 className="text-3xl text-white" style={serif}>{t.mapTitle}</h2>
            </div>
            <p className="text-white/40 mb-10 text-sm max-w-md leading-relaxed">{t.mapDesc}</p>
            <RealisticWorldMap lang={lang} />
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Contact;